/* eslint-disable no-console */
import {
  collection,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  getDocs,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  DeviceInfo,
  DeviceSessionState,
  TrustedDevice,
  SessionValidationResult,
  SESSION_CONSTANTS,
} from '../types';

const {
  STORAGE_KEYS,
  INACTIVITY_TIMEOUT_MS,
  SESSION_VERSION,
  MAX_TRUSTED_DEVICES,
} = SESSION_CONSTANTS;

/**
 * SessionService manages device trust and session persistence for web
 * Implements "Remember This Device" functionality for SMS 2FA
 */
export const SessionService = {
  /**
   * Check if localStorage is available (fails in incognito mode)
   */
  isLocalStorageAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  },

  /**
   * Get or generate device UUID (stored in localStorage)
   */
  async getDeviceId(): Promise<string> {
    try {
      if (!this.isLocalStorageAvailable()) {
        // Incognito mode - generate temporary UUID
        console.warn(
          '[SessionService] localStorage unavailable (incognito mode)'
        );
        return crypto.randomUUID();
      }

      // Try to retrieve existing device ID
      let deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);

      if (!deviceId) {
        // Generate new UUID v4
        deviceId = crypto.randomUUID();
        localStorage.setItem(STORAGE_KEYS.DEVICE_ID, deviceId);
        console.log('[SessionService] Generated new device UUID:', deviceId);
      }

      return deviceId;
    } catch (error) {
      console.error('[SessionService] Error getting device ID:', error);
      // Fallback to temp UUID
      return crypto.randomUUID();
    }
  },

  /**
   * Get device information for identification
   */
  async getDeviceInfo(): Promise<DeviceInfo> {
    const deviceId = await this.getDeviceId();
    const userAgent = navigator.userAgent;

    // Extract browser name from user agent
    let browserName = 'Unknown Browser';
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      browserName = 'Chrome';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      browserName = 'Safari';
    } else if (userAgent.includes('Firefox')) {
      browserName = 'Firefox';
    } else if (userAgent.includes('Edg')) {
      browserName = 'Edge';
    }

    const deviceName = `${browserName} on ${navigator.platform}`;

    return {
      deviceId,
      deviceName,
      deviceType: 'web',
      platform: navigator.platform,
    };
  },

  /**
   * Initialize session for a user (called on auth state change)
   */
  async initializeSession(userId: string): Promise<void> {
    try {
      if (!this.isLocalStorageAvailable()) {
        console.warn(
          '[SessionService] Cannot initialize session without localStorage'
        );
        return;
      }

      const deviceInfo = await this.getDeviceInfo();
      const now = Date.now();

      // Check if session exists
      const sessionData = localStorage.getItem(STORAGE_KEYS.SESSION_STATE);

      let existingSmsVerifiedAt = 0; // Default for new sessions

      if (sessionData) {
        const session: DeviceSessionState = JSON.parse(sessionData);

        // If session is for different user, clear it
        if (session.userId !== userId) {
          console.log('[SessionService] Different user, clearing old session');
          await this.clearSession();
          existingSmsVerifiedAt = 0; // Reset for new user
        } else {
          // Same user - preserve their SMS verification timestamp
          existingSmsVerifiedAt = session.smsVerifiedAt;
          console.log(
            '[SessionService] Preserving existing smsVerifiedAt:',
            existingSmsVerifiedAt
          );
        }
      }

      // Create or update session
      const sessionState: DeviceSessionState = {
        deviceId: deviceInfo.deviceId,
        deviceName: deviceInfo.deviceName,
        userId,
        smsVerifiedAt: existingSmsVerifiedAt, // Preserve existing or set to 0 for new
        lastActivityAt: now,
        sessionVersion: SESSION_VERSION,
      };

      localStorage.setItem(
        STORAGE_KEYS.SESSION_STATE,
        JSON.stringify(sessionState)
      );
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now.toString());

      console.log('[SessionService] Session initialized for user:', userId);
    } catch (error) {
      console.error('[SessionService] Error initializing session:', error);
    }
  },

  /**
   * Validate if current session is valid and whether SMS 2FA is needed
   */
  async validateSession(userId: string): Promise<SessionValidationResult> {
    try {
      if (!this.isLocalStorageAvailable()) {
        // Incognito mode - always require SMS 2FA
        return {
          isValid: false,
          needsSms2fa: true,
          reason: 'no_session',
        };
      }

      // Get local session state
      const sessionData = localStorage.getItem(STORAGE_KEYS.SESSION_STATE);

      if (!sessionData) {
        return {
          isValid: false,
          needsSms2fa: true,
          reason: 'no_session',
        };
      }

      const session: DeviceSessionState = JSON.parse(sessionData);

      // Check if session is for current user
      if (session.userId !== userId) {
        return {
          isValid: false,
          needsSms2fa: true,
          reason: 'logged_out',
        };
      }

      // Check if device has been SMS verified
      if (session.smsVerifiedAt === 0) {
        // Device not yet verified
        return {
          isValid: false,
          needsSms2fa: true,
          reason: 'new_device',
        };
      }

      // Check inactivity timeout (14 days)
      const now = Date.now();
      const timeSinceLastActivity = now - session.lastActivityAt;

      if (timeSinceLastActivity > INACTIVITY_TIMEOUT_MS) {
        console.log('[SessionService] Session expired due to inactivity');
        return {
          isValid: false,
          needsSms2fa: true,
          reason: 'inactivity_timeout',
        };
      }

      // Session is valid
      return {
        isValid: true,
        needsSms2fa: false,
      };
    } catch (error) {
      console.error('[SessionService] Error validating session:', error);
      // On error, require SMS 2FA to be safe
      return {
        isValid: false,
        needsSms2fa: true,
        reason: 'session_expired',
      };
    }
  },

  /**
   * Mark device as SMS verified and update timestamp
   */
  async markSmsVerified(_userId: string): Promise<void> {
    try {
      if (!this.isLocalStorageAvailable()) {
        return;
      }

      const sessionData = localStorage.getItem(STORAGE_KEYS.SESSION_STATE);

      if (!sessionData) {
        console.error('[SessionService] No session found to mark as verified');
        return;
      }

      const session: DeviceSessionState = JSON.parse(sessionData);
      const now = Date.now();

      session.smsVerifiedAt = now;
      session.lastActivityAt = now;

      localStorage.setItem(STORAGE_KEYS.SESSION_STATE, JSON.stringify(session));
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now.toString());

      console.log('[SessionService] Device marked as SMS verified');
    } catch (error) {
      console.error('[SessionService] Error marking SMS verified:', error);
    }
  },

  /**
   * Update last activity timestamp
   */
  async updateLastActivity(): Promise<void> {
    try {
      if (!this.isLocalStorageAvailable()) {
        return;
      }

      const now = Date.now();
      const sessionData = localStorage.getItem(STORAGE_KEYS.SESSION_STATE);

      if (sessionData) {
        const session: DeviceSessionState = JSON.parse(sessionData);
        session.lastActivityAt = now;
        localStorage.setItem(
          STORAGE_KEYS.SESSION_STATE,
          JSON.stringify(session)
        );
      }

      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now.toString());
    } catch (error) {
      console.error('[SessionService] Error updating last activity:', error);
    }
  },

  /**
   * Clear local session data (called on logout)
   */
  async clearSession(): Promise<void> {
    try {
      if (!this.isLocalStorageAvailable()) {
        return;
      }

      localStorage.removeItem(STORAGE_KEYS.SESSION_STATE);
      localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
      console.log('[SessionService] Session cleared');
    } catch (error) {
      console.error('[SessionService] Error clearing session:', error);
    }
  },

  /**
   * Trust this device (add to Firestore trusted devices)
   */
  async trustDevice(userId: string): Promise<void> {
    try {
      const deviceInfo = await this.getDeviceInfo();

      // Check if max devices reached
      const trustedDevices = await this.getTrustedDevices(userId);
      const activeDevices = trustedDevices.filter((d) => d.isActive);

      if (activeDevices.length >= MAX_TRUSTED_DEVICES) {
        // Find oldest device and remove it
        const oldestDevice = activeDevices.sort(
          (a, b) => a.lastActivityAt.toMillis() - b.lastActivityAt.toMillis()
        )[0];

        console.log(
          '[SessionService] Max devices reached, removing oldest:',
          oldestDevice.deviceId
        );
        await this.untrustDevice(userId, oldestDevice.deviceId);
      }

      // Add device to Firestore
      const deviceRef = doc(
        db,
        'users',
        userId,
        'trustedDevices',
        deviceInfo.deviceId
      );

      const trustedDevice: Omit<
        TrustedDevice,
        'firstTrustedAt' | 'lastSmsVerifiedAt' | 'lastActivityAt'
      > & {
        firstTrustedAt: any;
        lastSmsVerifiedAt: any;
        lastActivityAt: any;
      } = {
        deviceId: deviceInfo.deviceId,
        deviceName: deviceInfo.deviceName,
        deviceType: deviceInfo.deviceType,
        platform: deviceInfo.platform,
        firstTrustedAt: serverTimestamp(),
        lastSmsVerifiedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
        isActive: true,
        userAgent: navigator.userAgent,
      };

      await setDoc(deviceRef, trustedDevice, { merge: true });

      console.log(
        '[SessionService] Device trusted in Firestore:',
        deviceInfo.deviceId
      );
    } catch (error) {
      console.error('[SessionService] Error trusting device:', error);
    }
  },

  /**
   * Check if device is trusted in Firestore
   */
  async isDeviceTrusted(userId: string, deviceId: string): Promise<boolean> {
    try {
      const deviceRef = doc(db, 'users', userId, 'trustedDevices', deviceId);
      const deviceSnap = await getDoc(deviceRef);

      if (!deviceSnap.exists()) {
        return false;
      }

      const deviceData = deviceSnap.data() as TrustedDevice;
      return deviceData.isActive === true;
    } catch (error) {
      console.error('[SessionService] Error checking device trust:', error);
      return false;
    }
  },

  /**
   * Remove device trust (remote logout)
   */
  async untrustDevice(userId: string, deviceId: string): Promise<void> {
    try {
      const deviceRef = doc(db, 'users', userId, 'trustedDevices', deviceId);

      // Mark as inactive instead of deleting (for audit trail)
      await setDoc(deviceRef, { isActive: false }, { merge: true });

      console.log('[SessionService] Device untrusted:', deviceId);
    } catch (error) {
      console.error('[SessionService] Error untrusting device:', error);
    }
  },

  /**
   * Get all trusted devices for a user
   */
  async getTrustedDevices(userId: string): Promise<TrustedDevice[]> {
    try {
      const devicesRef = collection(db, 'users', userId, 'trustedDevices');
      const devicesSnap = await getDocs(devicesRef);

      const devices: TrustedDevice[] = [];
      devicesSnap.forEach((doc) => {
        devices.push(doc.data() as TrustedDevice);
      });

      return devices;
    } catch (error) {
      console.error('[SessionService] Error getting trusted devices:', error);
      return [];
    }
  },

  /**
   * Logout from all devices (mark all as inactive)
   */
  async logoutAllDevices(userId: string): Promise<void> {
    try {
      const devices = await this.getTrustedDevices(userId);

      for (const device of devices) {
        if (device.isActive) {
          await this.untrustDevice(userId, device.deviceId);
        }
      }

      // Clear local session
      await this.clearSession();

      console.log('[SessionService] Logged out from all devices');
    } catch (error) {
      console.error('[SessionService] Error logging out all devices:', error);
    }
  },
};
